import { useContext, useEffect, useState } from 'react';
import { Button } from 'reactstrap';
import { renderIcon } from '~/shared/components/icons';
import RegistrationsTable from './RegistrationsTable';
import { downloadCSV } from '~/services/csv';
import { fetchMyCampaign } from '~/services/campaign';
import { CampaignContext } from '~/context/campaign';
import {
    MyCampaignResponse,
    Registration,
    RegistrationDetail,
    RegistrationTotals,
} from '~/types/campaign';
import toast from 'react-hot-toast';
import { Link, useHistory, useParams } from 'react-router-dom';
import { fetchRegistrations } from '~/services/fundraisers';
import moment from 'moment';
import { AdminContext } from '~/context/admin';

const Registrations = () => {
    const [loading, setLoading] = useState<boolean>(true);
    const [{ campaign }, campaignDispatch] = useContext(CampaignContext);
    const [{ isAdmin, campaignId }, adminDispatch] = useContext(AdminContext);
    const history = useHistory();
    const { id } = useParams();
    const [fundraiserName, setFundraiserName] = useState<string>('');
    const [maxRegistrants, setMaxRegistrants] = useState<number | null>(null);
    const [registrations, setRegistrations] = useState<Registration[]>([]);
    const [registrationTotals, setRegistrationTotals] =
        useState<RegistrationTotals>();

    const getMyCampaign = async () => {
        try {
            const resp: MyCampaignResponse = await fetchMyCampaign();
            if (resp.detail) {
                if (resp.detail === 'Invalid token.') {
                    // Delete localStorage token
                    localStorage.removeItem('token');
                    history.push('/login');
                } else {
                    toast.error(resp.detail);
                }
            } else {
                campaignDispatch({
                    type: 'set_campaign',
                    payload: resp,
                });
                setLoading(false);
            }
        } catch (e) {
            console.log('getMyCampaign() e: ', e);
            // Show some error here
        }
    };

    useEffect(() => {
        if (!campaign) {
            getMyCampaign();
        } else {
            setLoading(false);
        }
    }, []);

    const getRegistrations = async () => {
        try {
            const resp = await fetchRegistrations(id, campaignId);
            console.log('resp: ', resp);
            if (resp.detail) {
                if (resp.detail === 'Invalid token.') {
                    // Delete localStorage token
                    localStorage.removeItem('token');
                    history.push('/login');
                } else {
                    toast.error(resp.detail);
                }
            } else {
                setFundraiserName(resp.fundraiser_name);
                setMaxRegistrants(resp.maximum_registrants ?? null);
                // Set registrations
                setRegistrations(resp.registrations);
                setRegistrationTotals({
                    item_totals: resp.item_totals,
                    additional_campaign_amounts:
                        resp.additional_campaign_amounts,
                });
            }
        } catch (e) {
            console.log('getRegistrations() e: ', e);
            // Show some error here
        }
    };

    useEffect(() => {
        if (campaign) {
            getRegistrations();
        }
    }, [campaign]);

    const getDetailSource = (detail: RegistrationDetail) => {
        if (detail.registration_option) {
            return {
                key: `option-${detail.registration_option.id}-${
                    detail.option_question_answer || ''
                }`,
                name: detail.registration_option.name,
                price: detail.registration_option.price,
            };
        }
        if (detail.registration_addon) {
            return {
                key: `addon-${detail.registration_addon.id}-${
                    detail.addon_option_selection || ''
                }`,
                name: detail.registration_addon.name,
                price: detail.registration_addon.price,
            };
        }
        if (detail.item) {
            return {
                key: `item-${detail.item.id}`,
                name: detail.item.name,
                price: detail.item.price,
            };
        }
        return {
            key: `price-${detail.price_paid}`,
            name: '',
            price: 0,
        };
    };

    const formatRegistrations = (registrations: Registration[]) => {
        const seenKeys = new Set<string>();
        const uniqueSources: { key: string; name: string; price: number }[] =
            [];
        registrations.forEach((registration) => {
            registration.registration_details.forEach((detail) => {
                const source = getDetailSource(detail);
                if (!seenKeys.has(source.key)) {
                    seenKeys.add(source.key);
                    uniqueSources.push(source);
                }
            });
        });
        return registrations.map((registration: Registration) => {
            const registrationDetails = uniqueSources.map((source, index) => {
                const registrationDetail =
                    registration.registration_details.find(
                        (detail) => getDetailSource(detail).key === source.key
                    );
                const unitPrice = registrationDetail?.price_paid
                    ? parseFloat(registrationDetail.price_paid)
                    : source.price ?? 0;
                return {
                    [`Ticket_Type_${index + 1}`]: source.name,
                    [`PriceEach_Type_${index + 1}`]: unitPrice,
                    [`Quantity_Type_${index + 1}`]:
                        registrationDetail?.quantity || 0,
                    [`LineCost_Type_${index + 1}`]:
                        registrationDetail?.quantity
                            ? unitPrice * registrationDetail.quantity
                            : 0,
                    [`OptionAnswer_Type_${index + 1}`]:
                        registrationDetail?.option_question_answer || '',
                    [`AddonSelection_Type_${index + 1}`]:
                        registrationDetail?.addon_option_selection || '',
                };
            });
            let tickets = 0;
            let totalTickets = 0;
            registration.registration_details.map((item) => {
                totalTickets = tickets += item.quantity;
            });
            const attendees = registration.registration_details.reduce(
                (acc, detail) => {
                    if (detail.registration_addon) return acc;
                    if (detail.registration_option) {
                        const perItem =
                            detail.registration_option.attendees_per_item ?? 1;
                        return acc + detail.quantity * perItem;
                    }
                    return acc + detail.quantity;
                },
                0
            );
            const total =
                parseFloat(registration.total) +
                parseFloat(registration.additional_campaign_amount);
            let registrationRow = {
                FirstName: registration.first_name,
                LastName: registration.last_name,
                AddressLine1: registration.address_1,
                AddressLine2: registration.address_2,
                City: registration.city,
                State: registration.state,
                Zip: registration.zip_code,
                Email: registration.email,
                Phone: registration.phone,
                RegistrationDate: moment(registration.created_date).format('L'),
                RegistrationAnswer: registration.registration_answer,
                AdditionalMessage: registration.message_to_organizer,
                AdditionalCampaignDonation:
                    registration.additional_campaign_amount,
                TotalTickets: totalTickets,
                Attendees: attendees,
                TotalCost: total,
                Status: registration.canceled ? 'Canceled' : 'Active',
                CanceledDate: registration.canceled_at
                    ? moment(registration.canceled_at).format('L')
                    : '',
            };
            registrationDetails.map((detail) => {
                registrationRow = { ...registrationRow, ...detail };
            });
            return registrationRow;
        });
    };

    return (
        <div className='container__inner'>
            <div className='mb-md-5'>
                <h1 className='h2'>Registrations for {fundraiserName}</h1>
                <Link
                    to='/fundraisers'
                    className='btn btn-link px-0 text-uppercase'
                >
                    {renderIcon('arrow_left')}
                    Return to Fundraisers
                </Link>
            </div>
            <div className='filter-bar justify-content-end align-items-center'>
                <Button
                    color='outline-dark'
                    size='sm'
                    onClick={() =>
                        downloadCSV(
                            formatRegistrations(registrations),
                            `Fundraiser Registrations`
                        )
                    }
                    disabled={registrations.length === 0}
                >
                    {renderIcon('download')} Export List
                </Button>
            </div>
            <div className='pb-5'>
                <RegistrationsTable
                    registrations={registrations}
                    registrationTotals={registrationTotals}
                    maxRegistrants={maxRegistrants}
                />
            </div>
        </div>
    );
};

export default Registrations;
